import { Fragment } from 'react';
import { Stack, Typography } from '@mui/material';
import {
    Campaign,
    CampaignChannelLabels,
    CampaignChannels,
    Send,
    SendRecurrenceFrequency,
    SplitActions,
    SplitTypes,
    ABTestType,
} from 'domains/campaigns/types';
import { CampaignStatus } from 'models/enums';
import ContentDetail from 'domains/content/components/ContentDetail';
import DetailItem from 'domains/core/components/DetailItem';
import useSegment from 'hooks/queries/useSegment';
import { getSplitDescription } from '../utils';
import { VoiceCallerDetailById } from 'domains/content/components/ContentDetail/ContentDetail';
import { formatDateTime } from 'domains/core/components/DateRangeText/DateRangeText';
import ABTestLabelBox, { getSendLabel } from 'domains/campaigns/components/ABTestLabelBox';
import ButtonContentPreview from 'domains/core/components/AutocompleteContent/ButtonContentPreview';
import useAppSendSenderProfileTemplate from 'hooks/queries/useAppSendSenderProfileTemplate';
import ABTestSlider from 'domains/campaigns/components/ABTestSlider';

// Mapping for A/B Test types to display-friendly labels
const AB_TEST_TYPE_LABELS = {
    [ABTestType.CONTENT]: 'content',
    [ABTestType.SEND_TIME]: 'send time',
} as const;

type ConditionalSplitProps = {
    campaign: Campaign;
    index: number;
    send: Send;
};

const ConditionalSplitDetailItem = ({ campaign, index, send }: ConditionalSplitProps) => {
    // This renders before a "meets criteria" split action. If the "meets criteria" action is exit,
    // we will try to use the segment id from the "does not meet criteria" action. If, for some reason,
    // the action is _also_ exit, there will be no segment id.
    const splitSegmentId = send?.segmentId || campaign.sends[index + 1]?.segmentId;
    const { data: segmentData } = useSegment(splitSegmentId, { enabled: !!splitSegmentId });

    const splitDescription = splitSegmentId ? `${getSplitDescription(segmentData)}` : 'no split description available';

    return <DetailItem header="Conditional Split" description={splitDescription} />;
};

type SendProps = {
    contentPreviewNodeId: string | number;
    handlePreviewClick: (id: number | null) => void;
    recurrenceFrequency: SendRecurrenceFrequency;
    send: Send;
    index: number;
    isSendTimeABTest?: boolean;
};

const SendDetailItem = ({
    contentPreviewNodeId,
    handlePreviewClick,
    recurrenceFrequency,
    send,
    index,
    isSendTimeABTest,
}: SendProps) => {
    const { channel } = send;

    const getHeader = () => {
        const sendChannel = CampaignChannelLabels[send?.channel];
        if (!send.splitType) return sendChannel;
        if (send.splitType === SplitTypes.MEETS) return 'Meets Criteria';
        return 'Does Not Meet Criteria';
    };
    const header = getHeader();

    function getContentId(channel: CampaignChannels, send: Send): number {
        switch (channel) {
            case CampaignChannels.DIRECT_MAIL:
                return send.directMailContentId;
            case CampaignChannels.SMS:
                return send.smsContentId || send.id;
            case CampaignChannels.VOICE_CALL:
                return send.voiceContentId;
            default:
                return send.id;
        }
    }

    if (send.splitAction === SplitActions.EXIT) {
        return <DetailItem key={`${send.splitType}-${index}`} header={header} description="exit curation" />;
    }

    const contentId = getContentId(channel, send);
    const isPreviewOpen = contentPreviewNodeId && send?.id === contentPreviewNodeId;
    const onClick = () => handlePreviewClick(isPreviewOpen ? null : send.id);

    if (channel === CampaignChannels.VOICE_CALL) {
        return (
            <>
                <ContentDetail
                    key={index}
                    channel={channel}
                    contentId={contentId}
                    header={header}
                    isPreviewOpen={isPreviewOpen}
                    scheduledEndTimestamp={send.scheduledEndTimestamp}
                    scheduledTimestamp={send.scheduledTimestamp}
                    onClick={onClick}
                />
                <VoiceCallerDetailById
                    callerId={send.voiceSenderProfileId}
                    isPreviewOpen={isPreviewOpen}
                ></VoiceCallerDetailById>
            </>
        );
    }

    const isSmsLegacy = channel === CampaignChannels.SMS && !send.smsContentId;

    return (
        <ContentDetail
            key={index}
            channel={channel}
            contentId={contentId}
            firstEmailRecurrenceFrequency={recurrenceFrequency}
            header={header}
            isCampaign
            isFirstEmail={index === 0}
            isPreviewOpen={isPreviewOpen}
            isSmsLegacy={isSmsLegacy}
            recurrenceFrequency={send.recurrenceFrequency}
            scheduledEndTimestamp={send.scheduledEndTimestamp}
            scheduledTimestamp={send.scheduledTimestamp}
            timeDelay={send.parentAudienceDelayInterval}
            isSendTimeABTest={isSendTimeABTest}
            onClick={onClick}
        />
    );
};

// Component for Content A/B Testing Email display
type ContentABTestEmailsProps = {
    sends: Send[];
    contentPreviewId?: string | number;
    handlePreviewClick: (contentNodeId: number) => void;
    channel: CampaignChannels;
    winnerSendId?: number;
};

const ContentABTestEmails = ({
    sends,
    contentPreviewId,
    handlePreviewClick,
    channel,
    winnerSendId,
}: ContentABTestEmailsProps) => (
    <DetailItem
        header="Email"
        description={
            <Stack spacing={1}>
                {sends.slice(0, 2).map((send, index) => {
                    const label = getSendLabel(index);
                    const isPreviewOpen = contentPreviewId && send?.id === contentPreviewId;
                    const onClick = () => handlePreviewClick(isPreviewOpen ? null : send.id);
                    const isWinner = winnerSendId && send.id === winnerSendId;

                    return (
                        <EmailABTestItem
                            key={`email-ab-${index}`}
                            send={send}
                            label={label}
                            index={index}
                            isPreviewOpen={isPreviewOpen}
                            onClick={onClick}
                            channel={channel}
                            isWinner={isWinner}
                        />
                    );
                })}
            </Stack>
        }
    />
);

// Winner indicator chip component
const WinnerIndicator = () => (
    <Typography
        component="span"
        variant="caption"
        sx={{
            color: 'success.main',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontWeight: 600,
        }}
    >
        (winner)
    </Typography>
);

// Individual Email A/B Test Item
type EmailABTestItemProps = {
    send: Send;
    label: string;
    index: number;
    isPreviewOpen: boolean;
    onClick: () => void;
    channel: CampaignChannels;
    isWinner?: boolean;
};

const EmailABTestItem = ({ send, label, index, isPreviewOpen, onClick, channel, isWinner }: EmailABTestItemProps) => {
    const { data: contentDetail, isSuccess: isContentDetailSuccess } = useAppSendSenderProfileTemplate(send.id, {
        enabled: Boolean(send.id),
    });

    const getEmailName = () => {
        if (!isContentDetailSuccess || !contentDetail) return `Email ${label}`;
        return contentDetail.templateName || `Email ${label}`;
    };

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
                <ABTestLabelBox label={label} size="small" />
                <Typography variant="body1">{getEmailName()}</Typography>
            </Stack>
            <ButtonContentPreview contentType={channel} isActive={isPreviewOpen} onClick={onClick} />
            {isWinner && <WinnerIndicator />}
        </Stack>
    );
};

type CustomCampaignDetailsProps = {
    campaign: Campaign;
    contentPreviewNodeId?: string | number;
    handlePreviewClick: (contentNodeId: number) => void;
};

const CustomCampaignDetails = ({ campaign, contentPreviewNodeId, handlePreviewClick }: CustomCampaignDetailsProps) => {
    // Check A/B testing types for EMAIL channel only
    const isContentABTest =
        campaign.abTestConfig?.testOn === ABTestType.CONTENT && campaign.channel === CampaignChannels.EMAIL;
    const isSendTimeABTest =
        campaign.abTestConfig?.testOn === ABTestType.SEND_TIME && campaign.channel === CampaignChannels.EMAIL;

    // Determine which sends to show in the main content area
    const firstSend = campaign.sends?.[0];
    const sendsToShow = isSendTimeABTest && firstSend ? [firstSend] : campaign.sends ?? [];

    return (
        <>
            {!isContentABTest &&
                sendsToShow.map((send: Send, index: number) => (
                    <Fragment key={`send-${index}`}>
                        {/* We want to add an indication of a split before split sends. At present, conditional splits
                    begin with meets criteria. This may change as splits become more complex. */}
                        {send.splitType === SplitTypes.MEETS && (
                            <ConditionalSplitDetailItem campaign={campaign} index={index} send={send} />
                        )}
                        <SendDetailItem
                            contentPreviewNodeId={contentPreviewNodeId}
                            handlePreviewClick={handlePreviewClick}
                            index={index}
                            recurrenceFrequency={firstSend?.recurrenceFrequency}
                            send={send}
                            isSendTimeABTest={isSendTimeABTest}
                        />
                    </Fragment>
                ))}
            {/* Show Content A/B Testing Email section */}
            {isContentABTest && campaign.sends?.length >= 2 && (
                <>
                    <ContentABTestEmails
                        sends={campaign.sends}
                        contentPreviewId={contentPreviewNodeId}
                        handlePreviewClick={handlePreviewClick}
                        channel={campaign.channel}
                        winnerSendId={campaign.abTestConfig?.winnerSendId}
                    />
                </>
            )}

            {campaign.abTestConfig && (
                <>
                    <DetailItem
                        header="A/B Testing"
                        description={AB_TEST_TYPE_LABELS[campaign.abTestConfig.testOn as ABTestType]}
                    />
                    <DetailItem
                        header="Test Size"
                        description={campaign.abTestConfig.testPercentage + '% audience'}
                        footer={
                            <>
                                <ABTestSlider
                                    segmentId={campaign.sends?.[0]?.segmentId}
                                    splitPercentage={campaign.abTestConfig.testPercentage as 50 | 100}
                                    campaignStatus={campaign.status}
                                />
                                {campaign.abTestConfig.testPercentage === 50 && (
                                    <Stack spacing={0} sx={{ marginTop: 1 }}>
                                        {campaign.abTestConfig.testEndsOn && (
                                            <Typography variant="body2" color="text.secondary">
                                                Test ends on {formatDateTime(campaign.abTestConfig.testEndsOn)}
                                            </Typography>
                                        )}
                                    </Stack>
                                )}
                            </>
                        }
                    />
                </>
            )}

            {/* Show single schedule for Content A/B testing */}
            {isContentABTest && (
                <DetailItem
                    header="Scheduled"
                    description={
                        <Stack spacing={0.5}>
                            <Typography variant="body1">{formatDateTime(firstSend?.scheduledTimestamp)}</Typography>
                            {/* Winner send information */}
                            {campaign.abTestConfig?.testPercentage === 50 && (
                                <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
                                    {!campaign.sends?.[2]
                                        ? 'Winner will be manually sent to remaining 50%'
                                        : campaign.status === CampaignStatus.WINNER_SCHEDULED
                                        ? `Winner will be sent on ${formatDateTime(
                                              campaign.sends?.[2]?.scheduledTimestamp
                                          )}`
                                        : campaign.status === CampaignStatus.WINNER_SENT
                                        ? `Winner sent Manually on ${formatDateTime(
                                              campaign.sends?.[2]?.scheduledTimestamp
                                          )}`
                                        : 'Winner will be manually sent to remaining 50%'}
                                </Typography>
                            )}
                        </Stack>
                    }
                />
            )}

            {/* Show Scheduled section for send time A/B testing */}
            {isSendTimeABTest && campaign.sends?.length >= 2 && (
                <DetailItem
                    header="Scheduled"
                    description={
                        <Stack spacing={0.5}>
                            {campaign.sends.slice(0, 2).map((send, index) => {
                                const label = getSendLabel(index);
                                const isWinner =
                                    campaign.abTestConfig?.winnerSendId &&
                                    send.id === campaign.abTestConfig.winnerSendId;
                                return (
                                    <Stack key={`schedule-${index}`} direction="row" alignItems="center" spacing={1}>
                                        <ABTestLabelBox label={label} size="small" />
                                        <Typography variant="body1">
                                            {formatDateTime(send?.scheduledTimestamp)}
                                        </Typography>
                                        {isWinner && <WinnerIndicator />}
                                    </Stack>
                                );
                            })}
                            {/* Winner send information */}
                            {campaign.abTestConfig?.testPercentage === 50 && (
                                <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
                                    {!campaign.sends?.[2]
                                        ? 'Winner will be manually sent to remaining 50%'
                                        : campaign.status === CampaignStatus.WINNER_SCHEDULED
                                        ? `Winner will be sent on ${formatDateTime(
                                              campaign.sends?.[2]?.scheduledTimestamp
                                          )}`
                                        : campaign.status === CampaignStatus.WINNER_SENT
                                        ? `Winner sent Manually on ${formatDateTime(
                                              campaign.sends?.[2]?.scheduledTimestamp
                                          )}`
                                        : 'Winner will be manually sent to remaining 50%'}
                                </Typography>
                            )}
                        </Stack>
                    }
                />
            )}
        </>
    );
};

export default CustomCampaignDetails;
